import { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";

const TYPE_COLORS = {
  incident_created: "text-sky-300",
  analysis_started: "text-blue-300",
  agent_hypothesis: "text-violet-300",
  consensus_computed: "text-emerald-300",
  runbook_generated: "text-emerald-300",
  approval_requested: "text-amber-300",
  approval_decision: "text-white",
};

function truncate(h) {
  if (!h) return "";
  return `${h.slice(0, 10)}…${h.slice(-6)}`;
}

function EventRow({ ev, idx }) {
  const [open, setOpen] = useState(false);
  const color = TYPE_COLORS[ev.event_type] || "text-zinc-300";
  return (
    <div
      className="border-l-2 border-white/10 pl-4 pb-4 last:pb-0"
      data-testid={`audit-event-${idx}`}
    >
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-start gap-2 text-left w-full"
      >
        {open ? (
          <ChevronDown className="h-3.5 w-3.5 mt-1 text-zinc-500 shrink-0" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 mt-1 text-zinc-500 shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`font-mono text-[10px] uppercase tracking-widest ${color}`}>
              {ev.event_type.replace(/_/g, " ")}
            </span>
            <span className="text-[10px] font-mono text-zinc-500">
              {new Date(ev.timestamp).toLocaleTimeString()}
            </span>
          </div>
          <div className="mt-0.5 text-sm text-zinc-200">{ev.summary}</div>
          <div className="mt-0.5 text-[11px] font-mono text-zinc-500">
            by {ev.actor}
          </div>
        </div>
      </button>

      {open ? (
        <div className="ml-5 mt-2 cre-terminal rounded-sm p-3 text-[11px]">
          <div className="yaml-comment"># hash chain</div>
          <div>
            <span className="yaml-key">prev_hash</span>:{" "}
            <span className="yaml-str">"{truncate(ev.prev_hash)}"</span>
          </div>
          <div>
            <span className="yaml-key">hash</span>:{" "}
            <span className="yaml-str">"{truncate(ev.hash)}"</span>
          </div>
          {ev.payload && Object.keys(ev.payload).length ? (
            <>
              <div className="yaml-comment mt-2"># payload</div>
              <pre className="whitespace-pre-wrap break-all">
                {JSON.stringify(ev.payload, null, 2)}
              </pre>
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export default function AuditTranscript({ events = [] }) {
  return (
    <div className="cre-surface rounded-sm p-6" data-testid="audit-transcript">
      <div className="flex items-center justify-between">
        <div>
          <div className="cre-overline">Immutable transcript</div>
          <h3 className="font-heading text-xl font-bold tracking-tight mt-1">
            Audit trail
          </h3>
        </div>
        <div className="text-[11px] font-mono text-zinc-500">
          hash-chained · {events.length} event{events.length === 1 ? "" : "s"}
        </div>
      </div>
      <div className="mt-4">
        {events.length === 0 ? (
          <div className="text-sm text-zinc-500">No events yet.</div>
        ) : (
          events.map((ev, i) => <EventRow key={ev.event_id} ev={ev} idx={i} />)
        )}
      </div>
    </div>
  );
}
